import { useMutation, useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { apiRelativePath, filenameFromDisposition, saveBlob } from '../client/download';
import { ApiError } from '../client/errors';
import { queryKeys } from '../client/query-client';
/**
 * Module certificates — the AUTOMATIC kind.
 *
 * ── Two different certificates, and this is not the other one ─────────────
 * `cohort.ts` carries the cohort certificate: one per member per cohort,
 * issued by a named person, gated, revocable, and described by the design as
 * *"reviewed and issued manually by the La Boussole team — not automatic"*.
 *
 * This is the one the same screen contrasts it against: *"issued automatically
 * the moment you finish every part of a Pro module"*. Different object,
 * different lifecycle, no overlap. Kept in its own file so nobody reaches for
 * whichever is nearest.
 *
 * ── The nouns, pinned ─────────────────────────────────────────────────────
 * The design says **module** where our API says `Course`, and **part** where it
 * says `CourseModule`. So `5 parts · 74 min` is one course with five modules.
 * The backend states the same mapping in the contract text; it is restated here
 * because every field below reads wrong under the other reading.
 *
 * ── Why this is a stored grant, in a block where nothing else is ──────────
 * `journey.ts` opens by saying nothing in the journey is stored — badges,
 * streaks and the score are all recomputed on read, because a stored grant
 * needs a writer to fire at the right moment and disagrees with reality when
 * it does not.
 *
 * A certificate is the exception, and it earns it by being **a document with a
 * number on it**. You cannot recompute a serial, and you cannot recompute what
 * a course looked like on the day it was issued.
 *
 * ── The PDF ───────────────────────────────────────────────────────────────
 * For a long while the serial was the whole artefact and this file said so.
 * `GET /api/certificates/{serial}/pdf` now renders the document on demand,
 * and every certificate object names its own route in `pdfPath` — see
 * `useDownloadCertificate` at the bottom.
 */
export const CourseCertificate = z.object({
    /** The verification number — what `GET /certificates/{serial}/verify` answers for. */
    serial: z.string(),
    issuedAt: z.string().datetime(),
    /**
     * How many parts the course held **when this was issued** — not how many it
     * holds now. The row's own `parts` is the current size, and the two diverge
     * the day an editor adds a tenth part.
     *
     * Render this one wherever the certificate is being described, so a document
     * keeps saying what it certified.
     */
    parts: z.number().int().nonnegative(),
    durationSeconds: z.number().int().nonnegative().nullable(),
    /**
     * Where the PDF is — `/api/certificates/LB-000042/pdf`, declared by the
     * server so no client assembles the route itself. Absolute, `/api` and all;
     * `apiRelativePath` turns it into what `HttpClient` takes.
     */
    pdfPath: z.string(),
});
export const CourseCertificateRow = z.object({
    courseId: z.string().uuid(),
    title: z.string(),
    /**
     * The course's own cover (22 Oct 2026) — the image its Library card draws.
     * Null when it has none; optional so a server a deploy behind still parses.
     */
    coverUrl: z.string().nullable().optional(),
    /** The course's size NOW. Compare progress against this one. */
    parts: z.number().int().nonnegative(),
    partsComplete: z.number().int().nonnegative(),
    durationSeconds: z.number().int().nonnegative().nullable(),
    completedAt: z.string().datetime().nullable(),
    /** `null` for every course not finished — which is most of the list. */
    certificate: CourseCertificate.nullable(),
});
/**
 * Its own envelope, not the shared paged one — `earned` is the reason.
 *
 * The count is of the WHOLE set rather than of the page, so a client holding
 * page one of three still reports the right number. Deriving it from `items`
 * would answer "certificates on this page", which is a different and usually
 * wrong number.
 */
export const CourseCertificateList = z.object({
    items: z.array(CourseCertificateRow),
    total: z.number().int().nonnegative(),
    page: z.number().int().positive(),
    pageSize: z.number().int().positive(),
    earned: z.number().int().nonnegative(),
    locale: z.string(),
});
/**
 * Every course the caller may list, certified or not.
 *
 * One read serves both surfaces: Pro Resources draws the whole grid including
 * its "Not started" rows, and Home's strip picks the closest to done out of the
 * same set. There is deliberately no `closestToDone` field — see
 * `closestToDone` below, which is one comparison over these rows and cannot
 * disagree with the list beside it.
 *
 * ── It reads EVERY page, and that is not an optimisation ──────────────────
 * This asked for `pageSize: 50` and rendered whatever came back. Measured
 * signed in as `pro1`, `GET /journey/certificates` answers `total: 83` — so
 * the grid drew 50 modules, said nothing, and 33 of the practitioner catalogue
 * were unreachable. There is no pagination control to reach them with: the
 * design draws one grid and no pager, because its `PRO_MODULES` is six rows
 * long.
 *
 * It went unnoticed through the TSP pass because tsp1's catalogue is nine
 * modules. A page-one bug is invisible until somebody has more than a page,
 * which is the second time this exact shape has bitten — the podcast bands
 * read page one of a 161-row list and dropped a series
 * (`04-parent-podcasts.md` §11).
 *
 * `closestToDone` makes it worse than a short grid: it picks the furthest-along
 * started course out of whatever rows it is handed, so on a truncated list the
 * Home strip does not merely omit a module, it *names the wrong one* with no
 * sign anything is missing.
 *
 * So: page until the set is exhausted. `pageSize` is 200 because that is the
 * server's validated maximum (201 answers `VALIDATION_FAILED`), which makes
 * today's 83 a single request — the loop is what keeps it correct at 201.
 *
 * `PAGE_LIMIT` is a runaway guard against a server that never stops advancing,
 * not a product cap. If it is ever hit the result is short, `total` still says
 * so, and the screen compares the two rather than quietly drawing less than it
 * was asked for.
 */
const PAGE_SIZE = 200;
const PAGE_LIMIT = 25;
/**
 * The loop, outside the hook so it can be tested without a QueryClient.
 *
 * Exported for its test and for nothing else — call the hook.
 */
export async function fetchAllCertificates(http, locale) {
    const first = await http.get('/journey/certificates', {
        query: { pageSize: PAGE_SIZE },
        schema: CourseCertificateList,
        locale,
    });
    const items = [...first.items];
    for (let page = 2; items.length < first.total && page <= PAGE_LIMIT; page += 1) {
        const next = await http.get('/journey/certificates', {
            query: { pageSize: PAGE_SIZE, page },
            schema: CourseCertificateList,
            locale,
        });
        // An empty page with rows still outstanding means the server disagrees with
        // its own `total`. Stop rather than spin to the guard.
        if (next.items.length === 0)
            break;
        items.push(...next.items);
    }
    /*
     * `total` and `earned` come off the FIRST page and are not recomputed. Both
     * describe the whole set by contract, and counting `earned` from `items`
     * would make the callout's number depend on how far the loop got — which is
     * precisely the failure being fixed.
     */
    return { ...first, items };
}
export function useCourseCertificates(http, locale, enabled = true) {
    return useQuery({
        queryKey: queryKeys.journey.certificates(locale),
        enabled,
        queryFn: () => fetchAllCertificates(http, locale),
    });
}
/**
 * `CLOSEST TO DONE` — the started-but-unfinished course furthest along.
 *
 * Started is the point: a course at 0 of 5 is not close to done, it is
 * untouched, and offering it under that heading would be the strip inventing
 * momentum. Ties break on the smaller course, since fewer parts remain.
 *
 * `null` when nothing is in flight — everything finished, or nothing begun.
 */
export function closestToDone(rows) {
    const started = rows.filter((r) => r.partsComplete > 0 && r.partsComplete < r.parts && r.parts > 0);
    if (started.length === 0)
        return null;
    return started.reduce((best, row) => {
        const a = row.partsComplete / row.parts;
        const b = best.partsComplete / best.parts;
        if (a !== b)
            return a > b ? row : best;
        return row.parts < best.parts ? row : best;
    });
}
/** What the server names the file, reproduced for when its header is unreadable. */
export function certificateFilename(serial) {
    return `la-boussole-certificate-${serial}.pdf`;
}
/**
 * The fetch-and-save, outside the hook so it can be tested with a fake
 * `HttpClient` and no QueryClient. Exported for that and for nothing else —
 * call the hook.
 */
export async function downloadCertificate(http, { pdfPath, serial }, locale) {
    const response = await http.getResponse(apiRelativePath(pdfPath), {
        locale,
        headers: { Accept: 'application/pdf' },
    });
    const blob = await response.blob();
    const filename = filenameFromDisposition(response.headers.get('Content-Disposition'), () => certificateFilename(serial));
    saveBlob(blob, filename);
    return { filename, bytes: blob.size };
}
export function useDownloadCertificate(http, locale) {
    return useMutation({
        mutationFn: (input) => downloadCertificate(http, input, locale),
    });
}
/**
 * The member-facing reason, as a key for the app to translate — the same
 * split as `archiveRefusalKey`, since `@lb/domain` holds no `t`.
 *
 * 403 `CERTIFICATE_NOT_YOURS` is another member's serial; 409
 * `CERTIFICATE_REVOKED` is a cohort certificate withdrawn since the screen
 * was drawn (the screen does not offer the button on one it knows is
 * revoked, so this is the race, not the norm). A 404 should be unreachable
 * from a button drawn off a live certificate, and a 401 has already survived
 * the client's refresh — both fall to the generic message.
 */
export function certificateRefusalKey(error) {
    if (error instanceof ApiError) {
        if (error.status === 403)
            return 'certificates.downloadNotYours';
        if (error.status === 409)
            return 'certificates.downloadRevoked';
    }
    return 'certificates.downloadFailed';
}
//# sourceMappingURL=certificates.js.map